import React, { useState } from 'react';
import {
  Card, CardContent, Typography, Box, IconButton,
  Menu, MenuItem, LinearProgress, Collapse, Divider, Button,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DownloadIcon from '@mui/icons-material/Download';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ScheduleIcon from '@mui/icons-material/Schedule';
import SyncIcon from '@mui/icons-material/Sync';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import { sanitizeErrorMessage } from '../../utils/errorMessage';

const STATUS_MAP = {
  completed:        { label: 'Completed',        color: '#22C55E', bg: 'rgba(34,197,94,0.12)',   icon: <CheckCircleIcon sx={{ fontSize: 13 }} /> },
  processing:       { label: 'Processing',        color: '#3B82F6', bg: 'rgba(59,130,246,0.12)',  icon: <SyncIcon sx={{ fontSize: 13 }} /> },
  pending:          { label: 'Pending',           color: '#3B82F6', bg: 'rgba(59,130,246,0.12)',  icon: <ScheduleIcon sx={{ fontSize: 13 }} /> },
  pending_approval: { label: 'Pending Approval',  color: '#F59E0B', bg: 'rgba(245,158,11,0.12)', icon: <ScheduleIcon sx={{ fontSize: 13 }} /> },
  approved:         { label: 'Approved',          color: '#22C55E', bg: 'rgba(34,197,94,0.12)',   icon: <CheckCircleIcon sx={{ fontSize: 13 }} /> },
  failed:           { label: 'Failed',            color: '#EF4444', bg: 'rgba(239,68,68,0.12)',   icon: <ErrorIcon sx={{ fontSize: 13 }} /> },
  error:            { label: 'Failed',            color: '#EF4444', bg: 'rgba(239,68,68,0.12)',   icon: <ErrorIcon sx={{ fontSize: 13 }} /> },
  rejected:         { label: 'Rejected',          color: '#EF4444', bg: 'rgba(239,68,68,0.12)',   icon: <ErrorIcon sx={{ fontSize: 13 }} /> },
  cancelled:        { label: 'Cancelled',         color: 'rgba(255,255,255,0.3)', bg: 'rgba(255,255,255,0.06)', icon: <CancelIcon sx={{ fontSize: 13 }} /> },
};

const getDisplayName = (job) => {
  if (job.project_name && job.project_name.trim() && job.project_name !== job.video_filename) {
    return job.project_name;
  }
  if (job.video_filename) {
    return job.video_filename
      .replace(/\.[^/.]+$/, '')
      .replace(/[_-]/g, ' ')
      .trim()
      .replace(/\b\w/g, l => l.toUpperCase()) || 'Untitled Project';
  }
  return `Job ${job.job_id?.slice(-6) || 'Unknown'}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const DetailRow = ({ label, value, mono }) => (
  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
    <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', color: 'rgba(255,255,255,0.35)', minWidth: 90, flexShrink: 0, pt: 0.1 }}>
      {label}
    </Typography>
    <Typography sx={{
      fontFamily: mono ? 'monospace' : 'Inter, sans-serif',
      fontSize: '12px',
      color: 'rgba(255,255,255,0.7)',
      wordBreak: 'break-all',
      lineHeight: 1.5,
    }}>
      {value}
    </Typography>
  </Box>
);

const DubbingJobCard = ({ job, onPlay, onDownload, onCancel, onDelete }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  const status = job.status?.toLowerCase() || 'unknown';
  const s = STATUS_MAP[status] || { label: status, color: 'rgba(255,255,255,0.4)', bg: 'rgba(255,255,255,0.06)', icon: <ScheduleIcon sx={{ fontSize: 13 }} /> };

  const isCompleted = status === 'completed';
  const isProcessing = status === 'processing' || status === 'pending' || status === 'approved';
  const isFailed = status === 'failed' || status === 'error' || status === 'rejected';
  const isCancelled = status === 'cancelled';

  return (
    <Card sx={{
      backgroundColor: '#0A0A0A',
      border: '1px solid rgba(255,255,255,0.06)',
      borderRadius: '12px',
      display: 'flex',
      flexDirection: 'column',
      transition: 'border-color 0.2s ease, transform 0.2s ease',
      '&:hover': {
        borderColor: 'rgba(255,255,255,0.12)',
        transform: 'translateY(-1px)',
      },
    }}>
      <CardContent sx={{ p: 2.5, pb: '20px !important', display: 'flex', flexDirection: 'column', gap: 0 }}>

        {/* Top row: icon + title + status + menu */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.75 }}>
          <Box sx={{
            width: 36, height: 36, borderRadius: '8px',
            backgroundColor: 'rgba(255,255,255,0.05)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <VideoFileIcon sx={{ fontSize: 18, color: 'rgba(255,255,255,0.3)' }} />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 600,
              fontSize: '14px',
              color: '#FFFFFF',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              mb: 0.25,
            }}>
              {getDisplayName(job)}
            </Typography>
            <Typography sx={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '11px',
              color: 'rgba(255,255,255,0.3)',
            }}>
              {formatDate(job.created_at)}
            </Typography>
          </Box>

          {/* Status badge */}
          <Box sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.5,
            px: 1, py: 0.4,
            borderRadius: '5px',
            backgroundColor: s.bg,
            color: s.color,
            fontFamily: 'Inter, sans-serif',
            fontSize: '11px',
            fontWeight: 600,
            letterSpacing: '0.01em',
            flexShrink: 0,
          }}>
            {s.icon}
            {s.label}
          </Box>

          <IconButton
            size="small"
            onClick={(e) => setAnchorEl(e.currentTarget)}
            sx={{ color: 'rgba(255,255,255,0.25)', flexShrink: 0, '&:hover': { color: 'rgba(255,255,255,0.6)' }, p: 0.5 }}
          >
            <MoreVertIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        {/* Language row */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <LangBadge label="Source" value={job.source_language?.toUpperCase() || 'AUTO'} />
          <ArrowForwardIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.2)' }} />
          <LangBadge label="Target" value={job.target_language?.toUpperCase() || 'N/A'} />
          {job.enable_lipsync && (
            <Box sx={{
              ml: 'auto', px: 1, py: 0.3, borderRadius: '5px',
              border: '1px solid rgba(255,255,255,0.1)',
              fontFamily: 'Inter, sans-serif', fontSize: '11px', fontWeight: 500,
              color: 'rgba(255,255,255,0.4)',
            }}>
              Lip-sync
            </Box>
          )}
        </Box>

        {/* Progress bar for processing */}
        {isProcessing && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', color: 'rgba(255,255,255,0.4)' }}>
                {job.current_step || 'Processing…'}
              </Typography>
              {job.progress !== undefined && (
                <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', fontWeight: 600, color: '#FFFFFF' }}>
                  {Math.round(job.progress || 0)}%
                </Typography>
              )}
            </Box>
            <LinearProgress
              variant="determinate"
              value={job.progress || 0}
              sx={{
                height: 3, borderRadius: 2,
                backgroundColor: 'rgba(255,255,255,0.08)',
                '& .MuiLinearProgress-bar': { background: '#FFFFFF', borderRadius: 2 },
              }}
            />
          </Box>
        )}

        {/* Error message */}
        {isFailed && (
          <Box sx={{
            mb: 2, px: 1.5, py: 1, borderRadius: '7px',
            backgroundColor: 'rgba(239,68,68,0.08)',
            border: '1px solid rgba(239,68,68,0.15)',
          }}>
            <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', color: '#EF4444', lineHeight: 1.4 }}>
              {sanitizeErrorMessage(job.error_message)}
            </Typography>
          </Box>
        )}

        {/* Action buttons */}
        {isCompleted && (
          <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
            <Button
              fullWidth
              startIcon={<PlayArrowIcon sx={{ fontSize: 16 }} />}
              onClick={() => onPlay(job)}
              sx={{
                backgroundColor: '#FFFFFF', color: '#000000',
                fontFamily: 'Inter, sans-serif', fontWeight: 600,
                fontSize: '13px', textTransform: 'none',
                borderRadius: '7px', py: 1,
                '&:hover': { backgroundColor: '#F0F0F0' },
              }}
            >
              Preview
            </Button>
            <Button
              fullWidth
              startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
              onClick={() => onDownload(job)}
              sx={{
                backgroundColor: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.1)',
                color: 'rgba(255,255,255,0.7)',
                fontFamily: 'Inter, sans-serif', fontWeight: 500,
                fontSize: '13px', textTransform: 'none',
                borderRadius: '7px', py: 1,
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)', color: '#FFFFFF' },
              }}
            >
              Download
            </Button>
          </Box>
        )}

        {/* Show Details toggle */}
        <Button
          onClick={() => setShowDetails(v => !v)}
          endIcon={showDetails ? <ExpandLessIcon sx={{ fontSize: 15 }} /> : <ExpandMoreIcon sx={{ fontSize: 15 }} />}
          sx={{
            color: 'rgba(255,255,255,0.3)',
            fontFamily: 'Inter, sans-serif',
            fontWeight: 500,
            fontSize: '12px',
            textTransform: 'none',
            justifyContent: 'center',
            py: 0.5,
            '&:hover': { color: 'rgba(255,255,255,0.6)', backgroundColor: 'transparent' },
          }}
        >
          {showDetails ? 'Hide Details' : 'Show Details'}
        </Button>

        <Collapse in={showDetails}>
          <Divider sx={{ my: 1.5, borderColor: 'rgba(255,255,255,0.06)' }} />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <DetailRow label="Job ID" value={job.job_id} mono />
            {job.video_filename && <DetailRow label="Original File" value={job.video_filename} />}
            {job.video_id && <DetailRow label="Video ID" value={job.video_id} mono />}
            {isCompleted && job.completed_at && (
              <DetailRow label="Completed" value={formatDate(job.completed_at)} />
            )}
            {job.updated_at && <DetailRow label="Last Updated" value={formatDate(job.updated_at)} />}
          </Box>
        </Collapse>
      </CardContent>

      {/* Context menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        PaperProps={{
          sx: {
            backgroundColor: '#111111',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: '8px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
            minWidth: 140,
          },
        }}
      >
        {isProcessing && (
          <MenuItem
            onClick={() => { setAnchorEl(null); onCancel(job); }}
            sx={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', color: '#F59E0B', gap: 1.5,
                  '&:hover': { backgroundColor: 'rgba(245,158,11,0.08)' } }}
          >
            <CancelIcon sx={{ fontSize: 16 }} /> Cancel Job
          </MenuItem>
        )}
        <MenuItem
          onClick={() => { setAnchorEl(null); onDelete(job); }}
          sx={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', color: '#EF4444', gap: 1.5,
                '&:hover': { backgroundColor: 'rgba(239,68,68,0.08)' } }}
        >
          <DeleteIcon sx={{ fontSize: 16 }} /> Delete
        </MenuItem>
      </Menu>
    </Card>
  );
};

const LangBadge = ({ label, value }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
    <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '11px', color: 'rgba(255,255,255,0.3)' }}>
      {label}
    </Typography>
    <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', fontWeight: 600, color: 'rgba(255,255,255,0.8)' }}>
      {value}
    </Typography>
  </Box>
);

export default DubbingJobCard;
